import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser
} from 'firebase/auth';
import { EmailPasswordAuthProvider, AuthUser } from './types';
import logger from 'src/logger';

export class EmailPasswordAuthService implements EmailPasswordAuthProvider {
  private auth = getAuth();

  async signIn(email: string, password: string): Promise<AuthUser | null> {
    try {
      const result = await signInWithEmailAndPassword(this.auth, email, password);
      return this.mapFirebaseUser(result.user, 'email-password');
    } catch (error) {
      logger.error('Email/password sign-in failed:', error);
      throw new Error('Email/password sign-in failed');
    }
  }

  async signUp(email: string, password: string): Promise<AuthUser | null> {
    try {
      const result = await createUserWithEmailAndPassword(this.auth, email, password);
      return this.mapFirebaseUser(result.user, 'email-password');
    } catch (error) {
      logger.error('Email/password sign-up failed:', error);
      throw new Error('Email/password sign-up failed');
    }
  }

  async signOut(): Promise<void> {
    try {
      await firebaseSignOut(this.auth);
    } catch (error) {
      logger.error('Email/password sign-out failed:', error);
      throw new Error('Email/password sign-out failed');
    }
  }

  getCurrentUser(): AuthUser | null {
    const user = this.auth.currentUser;
    if (!user) return null;
    return this.mapFirebaseUser(user, 'email-password');
  }

  async getIdToken(): Promise<string | null> {
    const user = this.auth.currentUser;
    if (!user) return null;
    return user.getIdToken();
  }

  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    return onAuthStateChanged(this.auth, (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        const user = this.mapFirebaseUser(firebaseUser, 'email-password');
        callback(user);
      } else {
        callback(null);
      }
    });
  }

  private mapFirebaseUser(firebaseUser: FirebaseUser, provider: string): AuthUser {
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
      accessToken: '', // Will be set when token is retrieved
      provider,
      providerData: {
        providerId: provider,
        uid: firebaseUser.uid,
        displayName: firebaseUser.displayName || '',
        email: firebaseUser.email || '',
        photoURL: firebaseUser.photoURL || ''
      }
    };
  }
}
